import { useMemo } from "react";
import { match } from "ts-pattern";
import { useCabinetStore } from "../../../context/StoreContext";
import { CompartmentModus, LayoutModus, useCanvasStore } from "../../../hooks/useCanvasStore";
import {
	canAddDoor,
	canAddDoubleDoor,
	canAddShelf,
	canChangeBackgroundDepth,
	canDistributeHorizontal,
	canDistributeVertical,
	canSplitHorizontally,
	canSplitVertically,
} from "../data";
import type { CompartmentType } from "../data/Compartment";

export enum ModeType {
	hover = "#6C6C6C",
	selected = "#285D6D",
	action = "#569310",
}

export default function useCompartment(compartment: CompartmentType) {
	const { state, api } = useCabinetStore();

	const { layoutMode, compartmentMode, hoveredCompartmentId, selectedCompartmentIds } =
		useCanvasStore();

	const isHovered = compartment.id === hoveredCompartmentId;

	const isSelected = useMemo(
		() => selectedCompartmentIds.some((id) => id === compartment.id),
		[compartment.id, selectedCompartmentIds],
	);

	const currentModeColor = useMemo(() => {
		if (isSelected) return ModeType.selected;

		if (layoutMode) {
			return match(layoutMode)
				.with(LayoutModus.addHorizontal, () =>
					canSplitHorizontally(compartment, state, api) ? ModeType.action : ModeType.hover,
				)
				.with(LayoutModus.addVertical, () =>
					canSplitVertically(compartment, state, api) ? ModeType.action : ModeType.hover,
				)
				.with(LayoutModus.distributeHorizontal, () =>
					canDistributeHorizontal(selectedCompartmentIds, compartment, state)
						? ModeType.action
						: ModeType.hover,
				)
				.with(LayoutModus.distributeVertical, () =>
					canDistributeVertical(selectedCompartmentIds, compartment, state)
						? ModeType.action
						: ModeType.hover,
				)
				.with(LayoutModus.background, () =>
					canChangeBackgroundDepth(compartment) ? ModeType.action : ModeType.hover,
				)
				.otherwise(() => ModeType.hover);
		}

		if (compartmentMode) {
			return match(compartmentMode)
				.with(CompartmentModus.DoorLeft, () =>
					canAddDoor(compartment, "left", state, api) ? ModeType.action : ModeType.hover,
				)
				.with(CompartmentModus.DoorRight, () =>
					canAddDoor(compartment, "right", state, api) ? ModeType.action : ModeType.hover,
				)
				.with(CompartmentModus.DoorDouble, () =>
					canAddDoubleDoor(compartment, state, api) ? ModeType.action : ModeType.hover,
				)
				.with(CompartmentModus.AddShelf, () =>
					canAddShelf(compartment) ? ModeType.action : ModeType.hover,
				)
				.with(CompartmentModus.RemoveShelf, () =>
					compartment.shelves.length > 0 ? ModeType.action : ModeType.hover,
				)
				.with(CompartmentModus.RemoveDoor, () =>
					compartment.door ? ModeType.action : ModeType.hover,
				)
				.otherwise(() => ModeType.hover);
		}

		return ModeType.hover;
	}, [compartment, layoutMode, compartmentMode, state, api, isSelected, selectedCompartmentIds]);

	return {
		canApplyCurrentMode: currentModeColor !== ModeType.hover,
		currentModeColor,
		isHovered,
		isSelected,
	};
}
